/**
 * Extract table names referenced in a compiled Kysely SQL string.
 *
 * Heuristic: Kysely emits double-quoted identifiers, so a regex over
 * `from "X"` / `join "X"` reliably surfaces table references in the *trivial*
 * select shapes used by the local SQLite client. CTE aliases introduced via
 * `with "alias" as (...)` are filtered out so they aren't treated as tables.
 *
 * This is **not** a SQL parser. Raw SQL fragments (`json_each(...)`,
 * `jsonb_array_elements(...)`), schema-qualified names, lateral joins, and
 * other shapes will produce nonsense or partial output. Queries that rely on
 * those shapes (notably anything reading from `patientFindingsView`) MUST
 * declare a `tables` override on `localQueryOptions` rather than depend on
 * the parser. The dev-mode drift check in `queryIndex.ts` warns when
 * declared and derived sets disagree.
 */

const FROM_OR_JOIN = /\b(?:from|join)\s+"([^"]+)"/gi
const CTE_ALIAS = /\bwith\s+(?:recursive\s+)?(?:"([^"]+)"|([a-z_][\w]*))\s+as\s*\(/gi

export function extractTablesFromSql(sql: string): string[] {
  if (!sql) return []

  const cteAliases = new Set<string>()
  for (const m of sql.matchAll(CTE_ALIAS)) {
    const alias = m[1] ?? m[2]
    if (alias) cteAliases.add(alias)
  }

  const tables = new Set<string>()
  for (const m of sql.matchAll(FROM_OR_JOIN)) {
    const name = m[1]
    if (!name) continue
    if (cteAliases.has(name)) continue
    tables.add(name)
  }

  return [...tables]
}
